import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import LoadingOverlay from './LoadingOverlay';
import Icon from './Icons';
import ErrorScreen from './ErrorScreen';
import { useConnectivity } from './hooks';
import { photoUrl } from './photoUtils';
import { plantDisplayName } from './nameUtils';
import IdentificationResults from './IdentificationResults';

// Module-level store removed — photos are now uploaded in AddPlant before
// navigating to PlantForm, so File objects never need to cross navigation boundaries.

const MAX_PHOTOS = 5;
const ORGAN_OPTIONS = [
  { value: 'leaf',   label: 'Leaf' },
  { value: 'flower', label: 'Flower' },
  { value: 'fruit',  label: 'Fruit' },
  { value: 'bark',   label: 'Bark' },
  { value: 'habit',  label: 'Habit (whole plant)' },
  { value: 'other',  label: 'Other' },
  { value: 'auto',   label: 'Auto-detect' },
];

const PHOTO_PART_FROM_ORGAN = {
  leaf: 'leaf',
  flower: 'flower',
  fruit: 'fruit',
  bark: 'stem_node',
  habit: 'full_plant',
};

function AddPlant() {
  const navigate = useNavigate();
  const location = useLocation();
  const connectivity = useConnectivity();
  const continuationState = location.state || {};
  const prefill = continuationState.prefill || {};

  // Each entry: { file: File, previewUrl: string, organ: string }
  const [photos, setPhotos] = useState([]);

  const [identifying, setIdentifying] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [quotaFallbackMsg, setQuotaFallbackMsg] = useState(null);
  const [error, setError] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [expandedDuplicate, setExpandedDuplicate] = useState(null);

  const atLimit = photos.length >= MAX_PHOTOS;
  const hasPhotos = photos.length > 0;

  // Fetch quota on mount so we can hide the organ selector when using Plant.id
  useEffect(() => {
    fetch('/id-quota')
      .then(r => r.ok ? r.json() : null)
      .then(q => { if (q) setQuotaInfo(q); })
      .catch(() => {});
  }, []);

  // -- Photo helpers ---------------------------------------------------------

  const addPhotosFromFiles = (fileList) => {
    const incoming = Array.from(fileList);
    const available = MAX_PHOTOS - photos.length;
    const toAdd = incoming.slice(0, available);

    const newEntries = toAdd.map(file => ({
      file,
      previewUrl: URL.createObjectURL(file),
      organ: 'leaf',
    }));

    setPhotos(prev => [...prev, ...newEntries]);
    setSuggestions(null);
    setError(null);
  };

  // Single handler — iOS shows native action sheet (Camera / Library / Files)
  const handleFileSelect = (e) => {
    if (!e.target.files?.length) return;
    addPhotosFromFiles(e.target.files);
    e.target.value = ''; // reset so same file can be re-selected
  };

  const handleOrganChange = (index, organ) => {
    setPhotos(prev => prev.map((p, i) => i === index ? { ...p, organ } : p));
  };

  const handleRemovePhoto = (index) => {
    setPhotos(prev => {
      URL.revokeObjectURL(prev[index].previewUrl); // avoid memory leak
      return prev.filter((_, i) => i !== index);
    });
    setSuggestions(null);
  };

  const handleReset = () => {
    photos.forEach(p => URL.revokeObjectURL(p.previewUrl));
    setPhotos([]);
    setSuggestions(null);
    setRemaining(null);
    setQuotaInfo(null);
    setQuotaFallbackMsg(null);
    setError(null);
    setDuplicates(null);
    setSelectedSuggestion(null);
  };

  // -- Plant ID / PlantNet identification ------------------------------------

  const handleIdentify = (forceProvider = null) => {
    if (photos.length === 0) return;

    setIdentifying(true);
    setError(null);
    if (!forceProvider) {
      setQuotaFallbackMsg(null);
      setQuotaInfo(null);
    }

    const formData = new FormData();
    photos.forEach(p => {
      formData.append('files', p.file);
      formData.append('organs', p.organ);
    });
    if (forceProvider) {
      formData.append('provider', forceProvider);
    }

    fetch('/identify-file', { method: 'POST', body: formData })
      .then(response => {
        if (response.status === 429) {
          return response.json().then(() => {
            setQuotaFallbackMsg('Daily limit reached — retrying with Standard identifier');
            handleIdentify('plantnet');
            return null;
          });
        }
        if (!response.ok) return response.json().then(err => { throw new Error(err.detail || 'Identification failed'); });
        return response.json();
      })
      .then(data => {
        if (!data) return;
        setSuggestions(data.top_suggestions);
        setRemaining(data.remaining_requests);
        setIdentifying(false);
        fetch('/id-quota')
          .then(r => r.ok ? r.json() : null)
          .then(q => { if (q) setQuotaInfo(q); })
          .catch(() => {});
      })
      .catch(err => {
        setError(err.message);
        setIdentifying(false);
      });
  };

  // -- Accept a suggestion — check for duplicates first --------------------

  const handleAccept = (suggestion) => {
    setSelectedSuggestion(suggestion);

    const genus = suggestion.genus || '';
    const speciesFull = suggestion.species || '';
    const speciesEpithet = speciesFull.replace(genus, '').trim();

    fetch(`/plants/check-duplicate?genus=${encodeURIComponent(genus)}&species=${encodeURIComponent(speciesEpithet)}`)
      .then(r => r.json())
      .then(matches => {
        if (matches.length > 0) {
          setDuplicates(matches);
        } else {
          proceedToForm(suggestion);
        }
      })
      .catch(() => proceedToForm(suggestion));
  };

  const proceedToForm = async (suggestion) => {
    const genus = suggestion.genus || '';
    const speciesFull = suggestion.species || '';
    const speciesEpithet = speciesFull.replace(genus, '').trim();
    const commonName = suggestion.common_names?.[0] || '';

    setIdentifying(true);
    setError(null);

    try {
      // Create the plant record first so we have an ID to upload photos to
      const plantRes = await fetch('/plants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          genus,
          species: speciesEpithet,
          common_name: commonName,
          status: prefill.status || 'collection',
          acquisition_date: prefill.acquisition_date || null,
          source: prefill.source || null,
          acquisition_notes: prefill.acquisition_notes || null,
        }),
      });
      if (!plantRes.ok) throw new Error('Failed to create plant');
      const savedPlant = await plantRes.json();

      // Upload all photos sequentially — avoids SQLite locking
      for (const photo of photos) {
        const formData = new FormData();
        formData.append('file', photo.file);
        if (PHOTO_PART_FROM_ORGAN[photo.organ]) formData.append('photo_part', PHOTO_PART_FROM_ORGAN[photo.organ]);
        const photoRes = await fetch(`/plants/${savedPlant.id}/photos`, {
          method: 'POST',
          body: formData,
        });
        if (!photoRes.ok) throw new Error('Failed to upload photo');
      }

      // Navigate to edit form — plant already exists, photos already uploaded.
      // PlantForm in edit mode lets user fill in remaining details.
      navigate(`/plants/${savedPlant.id}/edit`, {
        state: {
          ...continuationState,
          prefill: { ...prefill, genus, species: speciesEpithet, common_name: commonName },
          fromAddFlow: true,
        },
      });
    } catch (err) {
      setError(err.message);
      setIdentifying(false);
    }
  };

  // -- Add all photos to an existing plant ----------------------------------

  const handleAddToExisting = async (plantId) => {
    if (photos.length === 0) {
      navigate(`/plants/${plantId}`);
      return;
    }

    try {
      // Upload sequentially — avoids SQLite locking (matches server photo upload pattern)
      for (const photo of photos) {
        const formData = new FormData();
        formData.append('file', photo.file);
        if (PHOTO_PART_FROM_ORGAN[photo.organ]) formData.append('photo_part', PHOTO_PART_FROM_ORGAN[photo.organ]);
        const response = await fetch(`/plants/${plantId}/photos`, {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) throw new Error('Failed to upload photo');
      }
      navigate(`/plants/${plantId}`);
    } catch (err) {
      setError(err.message);
    }
  };

  // -- Other navigation helpers ---------------------------------------------

  const handleNewSpecimen = () => {
    setDuplicates(null);
    proceedToForm(selectedSuggestion);
  };

  const handleSkipWithPhoto = async () => {
    setIdentifying(true);
    setError(null);

    try {
      // Create a placeholder plant — user will fill in details in the edit form
      const plantRes = await fetch('/plants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          genus: 'Unknown',
          species: 'unknown',
          status: prefill.status || 'collection',
          acquisition_date: prefill.acquisition_date || null,
          source: prefill.source || null,
          acquisition_notes: prefill.acquisition_notes || null,
        }),
      });
      if (!plantRes.ok) throw new Error('Failed to create plant');
      const savedPlant = await plantRes.json();

      for (const photo of photos) {
        const formData = new FormData();
        formData.append('file', photo.file);
        if (PHOTO_PART_FROM_ORGAN[photo.organ]) formData.append('photo_part', PHOTO_PART_FROM_ORGAN[photo.organ]);
        const photoRes = await fetch(`/plants/${savedPlant.id}/photos`, {
          method: 'POST',
          body: formData,
        });
        if (!photoRes.ok) throw new Error('Failed to upload photo');
      }

      navigate(`/plants/${savedPlant.id}/edit`, {
        state: { ...continuationState, fromAddFlow: true },
      });
    } catch (err) {
      setError(err.message);
      setIdentifying(false);
    }
  };

  // -- Render ----------------------------------------------------------------

  if (connectivity === false) return <div className="app"><ErrorScreen title="No connection" message="Load failed" /></div>;
  if (connectivity === null)  return <div className="app"><p className="health-loading">Checking connection…</p></div>;

  return (
    <div className="app">
      <header className="sub-header">
        <button className="sub-header-back" onClick={() => navigate('/', { replace: true })} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Add New Plant</h1>
        </div>
      </header>

      {/* -- Entry point choices (before any photo is added) ---------------- */}
      {!hasPhotos && (
        <div className="add-plant-choices">
          <label className="add-plant-choice add-plant-choice-primary">
            <span className="add-plant-choice-icon"><Icon name="camera" size={28} /></span>
            <span className="add-plant-choice-title">Take a Photo</span>
            <span className="add-plant-choice-desc">Use your camera — you can add more after</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </label>

          <label className="add-plant-choice">
            <span className="add-plant-choice-icon"><Icon name="image" size={28} /></span>
            <span className="add-plant-choice-title">Choose from Gallery</span>
            <span className="add-plant-choice-desc">Select up to {MAX_PHOTOS} photos to identify</span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </label>

          <Link to="/new/manual" state={continuationState} className="add-plant-choice" style={{ textDecoration: 'none' }}>
            <span className="add-plant-choice-icon"><Icon name="pencil" size={28} /></span>
            <span className="add-plant-choice-title">Enter Manually</span>
            <span className="add-plant-choice-desc">Fill in plant details yourself</span>
          </Link>
        </div>
      )}

      {/* -- Photo strip + ID flow ----------------------------------------- */}
      {hasPhotos && (
        <div className="detail-section">
          <div style={{ position: 'relative' }}>
            <LoadingOverlay isLoading={identifying} message="Identifying plant..." />
          </div>

          {!suggestions && (
            <>
              {/* Photo list — organ selector shown only for PlantNet (auto-detects for Plant.id) */}
              <div className="id-photo-list">
                {photos.map((p, i) => (
                  <div key={i} className="id-photo-row">
                    <img src={p.previewUrl} alt={`Selected plant ${i + 1}`} className="id-photo-thumb" />
                    {quotaInfo?.provider !== 'plantid' && (
                      <label className="id-photo-organ-field">
                        <span>PlantNet ID part</span>
                        <select
                          className="form-input id-photo-organ"
                          value={p.organ}
                          onChange={e => handleOrganChange(i, e.target.value)}
                        >
                          {ORGAN_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </label>
                    )}
                    <button
                      type="button"
                      className="id-photo-remove"
                      onClick={() => handleRemovePhoto(i)}
                      aria-label="Remove photo"
                      title="Remove photo"
                    >
                      <Icon name="x" size={14} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add more / limit message */}
              {atLimit ? (
                <p className="id-limit-msg">Maximum {MAX_PHOTOS} photos reached.</p>
              ) : (
                <label className="id-add-more-btn">
                  <Icon name="camera" size={14} style={{marginRight: 6}} />Add another photo
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </label>
              )}

              {/* Primary actions */}
              <div className="id-flow-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleIdentify}
                  disabled={identifying}
                >
                  {identifying
                    ? <><Icon name="clock" size={14} style={{marginRight:4}} />Identifying...</>
                    : `Identify Species (${photos.length} photo${photos.length > 1 ? 's' : ''})`}
                </button>
                <button className="btn btn-secondary" onClick={handleSkipWithPhoto}>
                  Skip ID <Icon name="arrowRight" size={13} style={{margin:"0 2px"}} /> Enter Manually
                </button>
                <button className="btn btn-secondary" onClick={handleReset}>
                  Start Over
                </button>
              </div>
            </>
          )}

          {error && <p className="error id-error">{error}</p>}

          {quotaFallbackMsg && (
            <p className="settings-save-status" style={{ margin: '0.5rem 0' }}>{quotaFallbackMsg}</p>
          )}

          {/* Results via shared component */}
          {suggestions && (
            <IdentificationResults
              suggestions={suggestions}
              refImages={[]}
              quotaInfo={quotaInfo}
              provider={quotaInfo?.provider || 'plantnet'}
              remainingRequests={remaining}
              onAccept={handleAccept}
              onDismiss={handleSkipWithPhoto}
              dismissLabel="None of these — Enter Manually"
              emptyDismissLabel="Enter Manually Instead"
              onRetry={() => { setSuggestions(null); setError(null); }}
            />
          )}

          {/* Duplicate detection panel */}
          {duplicates && duplicates.length > 0 && (
            <div className="detail-section duplicate-panel">
              <h3 className="section-title">Plant Already in Collection</h3>
              <p className="duplicate-intro">
                You already have {duplicates.length} <em>{selectedSuggestion?.species}</em> in your collection. Is this the same plant or a new specimen?
              </p>

              <div className="duplicate-matches">
                {duplicates.map(d => (
                  <div key={d.id} className="duplicate-match-wrapper">
                    <div className="duplicate-match">
                      {d.latest_photo_id ? (
                        <img
                          src={photoUrl(d.latest_photo_id, d.latest_photo_rotation_version, d.latest_photo_created_at)}
                          alt={plantDisplayName(d) || d.specimen_id}
                          className="duplicate-match-photo"
                          onClick={() => setExpandedDuplicate(expandedDuplicate === d.id ? null : d.id)}
                        />
                      ) : (
                        <div className="duplicate-match-photo-empty"><Icon name="sprout" size={24} /></div>
                      )}
                      <div className="duplicate-match-info">
                        <span className="duplicate-match-name">{plantDisplayName(d) || d.specimen_id}</span>
                        <span className="duplicate-match-id">{d.specimen_id}</span>
                        <span className={`status-badge status-${d.status}`}>{d.status}</span>
                      </div>
                      <div className="duplicate-match-actions">
                        <button className="btn btn-primary btn-sm" onClick={() => handleAddToExisting(d.id)}>
                          Same plant — add photo{photos.length > 1 ? 's' : ''}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setExpandedDuplicate(expandedDuplicate === d.id ? null : d.id)}>
                          {expandedDuplicate === d.id ? 'Collapse' : 'Details'}
                        </button>
                      </div>
                    </div>

                    {expandedDuplicate === d.id && (
                      <div className="duplicate-expanded">
                        {d.latest_photo_id && (
                          <img
                            src={photoUrl(d.latest_photo_id, d.latest_photo_rotation_version, d.latest_photo_created_at)}
                            alt={plantDisplayName(d) || d.specimen_id}
                            className="duplicate-expanded-photo"
                          />
                        )}
                        <div className="duplicate-expanded-details">
                          {d.acquisition_date && <span className="detail-tag">Acquired: {d.acquisition_date}</span>}
                          {d.growing_method && <span className="detail-tag">{d.growing_method}</span>}
                          {d.substrate && d.substrate !== d.growing_method && <span className="detail-tag">{d.substrate}</span>}
                          {d.locations?.map((loc, i) => <span key={i} className="detail-tag location-tag">{loc}</span>)}
                          {d.last_observation_date && <span className="detail-tag">Last observation: {d.last_observation_date}</span>}
                          {d.photo_count > 0 && <span className="detail-tag"><Icon name="camera" size={12} style={{marginRight:3}} />{d.photo_count} photos</span>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="duplicate-actions">
                <button className="btn btn-secondary" onClick={handleNewSpecimen}>
                  Different specimen — create new
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AddPlant;
